import React, { useMemo } from 'react';
import { formatCurrency } from '../lib/api';

/**
 * Calendar heatmap of daily spending for a given month.
 * heatmap = [{ date: "2026-05-08", amount: 42.5 }, ...]
 */
const SpendingHeatmap = ({ heatmap = [], month, year }) => {
  const { cells, max, total } = useMemo(() => {
    const map = new Map(heatmap.map((d) => [d.date, d.amount]));
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstWeekday = new Date(year, month - 1, 1).getDay(); // 0=Sun, 1=Mon...
    const padStart = firstWeekday === 0 ? 6 : firstWeekday - 1; // start week on Monday
    const cells = [];
    for (let i = 0; i < padStart; i++) cells.push({ empty: true, key: `e${i}` });
    let max = 0;
    let total = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const amount = map.get(date) || 0;
      if (amount > max) max = amount;
      total += amount;
      cells.push({ day: d, date, amount });
    }
    return { cells, max, total };
  }, [heatmap, month, year]);

  const intensity = (amount) => {
    if (!amount) return 'bg-muted/40';
    const ratio = max ? amount / max : 0;
    if (ratio < 0.2) return 'bg-emerald-200/70 dark:bg-emerald-900/40';
    if (ratio < 0.4) return 'bg-emerald-300 dark:bg-emerald-800/60';
    if (ratio < 0.6) return 'bg-emerald-400 dark:bg-emerald-700/70';
    if (ratio < 0.8) return 'bg-emerald-500 dark:bg-emerald-600/80';
    return 'bg-emerald-600 dark:bg-emerald-500';
  };

  const weekdays = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

  return (
    <div data-testid="spending-heatmap">
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekdays.map((w, i) => (
          <div key={i} className="text-[10px] uppercase tracking-wide text-muted-foreground text-center font-medium">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c) =>
          c.empty ? (
            <div key={c.key} className="aspect-square" />
          ) : (
            <div
              key={c.date}
              title={`${c.date} · ${formatCurrency(c.amount)}`}
              className={`aspect-square rounded-md ${intensity(c.amount)} flex items-center justify-center text-[11px] font-medium transition-transform hover:scale-110 hover:ring-2 hover:ring-primary/30 cursor-default`}
            >
              <span className={c.amount > max * 0.5 ? 'text-white' : 'text-foreground/70'}>
                {c.day}
              </span>
            </div>
          )
        )}
      </div>
      <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
        <span>Tot. mese: <span className="font-medium text-foreground tabular-nums">{formatCurrency(total)}</span></span>
        <div className="flex items-center gap-1">
          <span>meno</span>
          {[0.1, 0.3, 0.5, 0.7, 0.9].map((r) => (
            <div key={r} className={`w-3 h-3 rounded ${intensity(max * r)}`} />
          ))}
          <span>più</span>
        </div>
      </div>
    </div>
  );
};

export default SpendingHeatmap;
